import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GeneralSelectionFacade } from '../../general-selection/facade/general-selection.facade';
import {computed, Injectable} from "@angular/core";
import {combineLatest, filter, map} from "rxjs";
import {ICashFlowAvailableDataObject, ICashflowAvailableDataResult, ICashflowRequest} from "src/app/models/cashflow";
import { CashflowService } from 'src/app/services/http/cashflow.service';
import { IPortfolio } from 'src/app/models/portfolios';
import { ClientEntity } from 'src/app/models/clients';
import {toSignal} from "@angular/core/rxjs-interop";
import {AppConfig} from "../../../app.config";

@UntilDestroy()
@Injectable()
export class CashflowFacade {

    public cashflowData$ = this.cashflowService.cashflowAvailableResponse$;


    public cashflowByCurrency$ = this.cashflowData$.pipe(map(c => {
        const children = c?.cashflowAvailableData?.children ?? [];

        const currencyData = children
            .flatMap(child => child.children)
            .map(c => c.data);

        return this.aggregateCashByCurrency(currencyData);
    }));

    /**
     *  Signal representation of cashFlowData$
     */
    public cashflow = toSignal(
        this.cashflowData$
            .pipe(
                untilDestroyed(this),
                filter(result => this.isCashflowDataPopulated(result) && result.isLatest),
                map(result => result.cashflowAvailableData.data)
            )
    );

    /**
     *  Signal representation of cashflowByCurrency
     */
    private cashflowByCurrency = toSignal(this.cashflowByCurrency$);

    protected hasAndSupportsMultipleCurrenciesForTrading = computed(() => this.cashflowByCurrency().length > 1 && this.appConfig.appConfigSignal()?.features?.buySell?.allowMultipleCurrencies);
        

    constructor(
        private generalSelectionFacade: GeneralSelectionFacade,
        private cashflowService: CashflowService,
        private appConfig: AppConfig) { }

    /**
     * Requests cashflow available data for values set in the general selection state
     */
    public requestAvailableCashflowData(isLatest: boolean = false) : void {
        combineLatest([
            this.generalSelectionFacade.getSelectedEntityPortfolioState(),
            this.generalSelectionFacade.getSelectedDate()
        ])
        .pipe(untilDestroyed(this))
        .subscribe(([generalState, selectedDate]) => {
            //If we are requesting the latest cash available data we want to override the 'selectedToDate' as we are always requesting the data for to todays date.
            if(isLatest === true)
                selectedDate = new Date();

            let paramsObject = {
                clientEntities: generalState.selectedEntities,
                date: selectedDate,
                portfolios: generalState.selectedPortfolios,
                isLatest: isLatest
            } as ICashflowRequest;

            this.requestAvailableCashflowDataInternal(paramsObject);
        })
    }

    /**
     * Requests cashflow available data for provided parameters
     */
    public requestAvailableCashflowDataWithParams(selectedEntities: ClientEntity[], selectedDate: Date, selectedPortfolios: IPortfolio[] = [], isLatest: boolean = false){
        if (!!!selectedEntities || selectedEntities.length < 1)
            return;

        let paramsObject = {
            clientEntities: selectedEntities,
            date: selectedDate,
            portfolios: selectedPortfolios,
            isLatest: isLatest
        } as ICashflowRequest;

        this.requestAvailableCashflowDataInternal(paramsObject);
    }


    /**
     * Gets the available cash for trading. If the portal supports multiple currencies, then the available cash
     * for the specified is returned. If the portal does not support multiple currencies, then the available cash
     * in the portfolios reference currency is returned.
     */
    public getAvailableCashForTrading(currency: string){
        const cashflowRoot = this.cashflow();
        const breakdowns = this.cashflowByCurrency();

        if (!this.hasAndSupportsMultipleCurrenciesForTrading()) {
            return cashflowRoot;
        }

        return breakdowns.find(b => b.CurrencyName === currency);
    }

    private requestAvailableCashflowDataInternal(cashflowRequest: ICashflowRequest) {
        // if we have a mismatch on our entities and portfolios then don't request anything
        if(cashflowRequest.portfolios && cashflowRequest.portfolios.length > 0){
            let entityIds = cashflowRequest.clientEntities.map(e => e.EntityId);
            if(!cashflowRequest.portfolios.some(p => entityIds.includes(p.entityId)))
                return;
        }
        
        this.cashflowService.getCashAvailableData(cashflowRequest);
    };

    private aggregateCashByCurrency(records: ICashFlowAvailableDataObject[]): ICashFlowAvailableDataObject[] {
        const grouped: Record<number, ICashFlowAvailableDataObject> = {};

        for (const record of records) {
            const currencyKey = record.Currency ?? -1;

            if (!grouped[currencyKey]) {
                // EntityId and PortfolioId are zero because these cash balances are technically aggregated across
                // portfolios if you have multiple
                grouped[currencyKey] = {
                    EntityId: 0,
                    PortfolioId: 0,
                    Caption: record.CurrencyName,
                    Currency: record.Currency,
                    CurrencyName: record.CurrencyName,
                    CashAvailable: 0,
                    CashBalances: 0,
                    IncomePending: 0,
                    OutgoingsPending: 0,
                    UnsettledTrades: 0,
                    QuarantinedCash: 0
                };
            }

            grouped[currencyKey].CashAvailable += record.CashAvailable ?? 0;
            grouped[currencyKey].CashBalances += record.CashBalances ?? 0;
            grouped[currencyKey].IncomePending += record.IncomePending ?? 0;
            grouped[currencyKey].OutgoingsPending += record.OutgoingsPending ?? 0;
            grouped[currencyKey].UnsettledTrades += record.UnsettledTrades ?? 0;
            grouped[currencyKey].QuarantinedCash += record.QuarantinedCash ?? 0;
        }

        return Object.values(grouped);
    }

    /**
     * Checks if cashflow data is properly populated
     */
    private isCashflowDataPopulated(cashflowAvailableResult: ICashflowAvailableDataResult): boolean {
        return cashflowAvailableResult.cashflowAvailableData?.children?.length > 0;
    }
}